import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@environment';
import { Observable } from 'rxjs';
import { IApiResponse } from '@app/api/interfaces/common/api-response.interface';
import { IListFilter } from '@app/api/interfaces/common/list-filter';
import { IPaginationMeta, IPaginationRequest } from '@app/api/interfaces/common/pagination-meta.interface';
import { prepareParamsForRequest } from '@app/core/methods/http-params';
import { SplitRequestService } from '@app/core/services/split-request-by-limit/split-request.service';
import { ICorrelation, IUpdateCorrelationRequest } from '@app/api/interfaces/correlation.interface';
import { IComment, ICreateCommentRequest, IUpdateCommentRequest } from '@app/api/interfaces/comment.interface';

@Injectable({
  providedIn: 'root',
})
export class CorrelationsApiService {
  constructor(
    private readonly httpClient: HttpClient,
    private readonly splitRequestService: SplitRequestService
  ) {}

  public getByProjectId(
    projectId: string,
    filter: IListFilter = {},
    pagination: Partial<IPaginationRequest> = {
      limit: 1000,
    },
    all = true
  ): Observable<IApiResponse<ICorrelation[], IPaginationMeta>> {
    const url = `${environment.apiBaseUrl}/v1/projects/${projectId}/correlations`;

    const filterForParams = prepareParamsForRequest(filter);

    const params = new HttpParams({
      fromObject: {
        ...(filterForParams || {}),
        ...(pagination || {}),
      },
    });

    const options = {
      params,
      withCredentials: true,
    };

    if (all) {
      return this.splitRequestService.getInParallel<ICorrelation, IPaginationMeta>(url, options);
    } else {
      return this.httpClient.get<IApiResponse<ICorrelation[], IPaginationMeta>>(url, options);
    }
  }

  public getOne(projectId: string, correlationId: string): Observable<IApiResponse<ICorrelation, object>> {
    const url = `${environment.apiBaseUrl}/v1/projects/${projectId}/correlations/${correlationId}`;

    return this.httpClient.get<IApiResponse<ICorrelation, object>>(url, {
      withCredentials: true,
    });
  }

  public update(
    projectId: string,
    correlationId: string,
    body: IUpdateCorrelationRequest
  ): Observable<IApiResponse<ICorrelation, object>> {
    const url = `${environment.apiBaseUrl}/v1/projects/${projectId}/correlations/${correlationId}`;

    return this.httpClient.patch<IApiResponse<ICorrelation, object>>(url, body, {
      withCredentials: true,
    });
  }

  public createComment(
    projectId: string,
    correlationId: string,
    body: ICreateCommentRequest
  ): Observable<IApiResponse<IComment, object>> {
    const url = `${environment.apiBaseUrl}/v1/projects/${projectId}/correlations/${correlationId}/comment`;

    return this.httpClient.post<IApiResponse<IComment, object>>(url, body, {
      withCredentials: true,
    });
  }

  public updateComment(
    projectId: string,
    correlationId: string,
    body: IUpdateCommentRequest
  ): Observable<IApiResponse<IComment, object>> {
    const url = `${environment.apiBaseUrl}/v1/projects/${projectId}/correlations/${correlationId}/comment`;

    return this.httpClient.patch<IApiResponse<IComment, object>>(url, body, {
      withCredentials: true,
    });
  }
}
